import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import {
  LayoutDashboard,
  Settings,
  Ticket,
  LogOut,
  ChevronLeft,
  Eye,
  Database,
  Users,
} from "lucide-react";
import profileimg from "@/assets/profile-image.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/Avatar";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";
import { useToast } from "../UseToast";
import { logout } from "@/features/auth/AuthSlice";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Separator } from "../Separator";
import { useDeleteSessionsMutation } from "@/services/UserApi";

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  userId?:number;
}

export function Sidebar({
  className,
  isCollapsed = false,
  setIsCollapsed,
  userId
}: SidebarProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [deleteSession] = useDeleteSessionsMutation();
  const { userInfo } = useSelector((state: RootState) => state.auth);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple clicks
    setIsLoggingOut(true); // Start loading
    try {
      await deleteSession({
        user_id: userId ?? 0,
        fcm_token: localStorage.getItem("fcm_token") ?? "",
      });
      dispatch(logout());
      toast({
        variant: "success",
        title: "Logout",
        description: "You are Successfully Logged Out",
      });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
      toast({
        variant: "destructive",
        title: "Logout Failed",
        description: "Something went wrong, please try again.",
      });
    } finally {
      setIsLoggingOut(false); // Stop loading
    }
  };

  
   const handleEditProfile = () => {
     navigate(`/user/edit-profile/${userId}`); // Navigate to the edit profile page
     setIsCollapsed(true);
   };
  return (
    <div
      className={cn(
        "fixed top-0 left-0 h-full transition-transform duration-300 z-50",
        isCollapsed ? "" : "translate-x-0",
        isCollapsed ? "hidden" : "",
        className
      )}
    >
      <Button
        variant="ghost"
        className={cn(
          "h-6 w-6 absolute right-[-12px] top-7 z-50 rounded-full p-0 hover:bg-muted bg-gray-400",
          isCollapsed && "rotate-180"
        )}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div
        className={cn(
          "flex flex-col h-full bg-white border-r px-4 pt-4 shadow-md transition-all duration-300",
          isCollapsed ? "w-0" : "w-[280px]"
        )}
      >
        {/* Profile Section */}
        <div className="flex items-center gap-4 pb-6">
          <Avatar className="h-12 w-12">
            <AvatarImage src={profileimg} />
            <AvatarFallback>
              {userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}
            </AvatarFallback>
          </Avatar>
          <div
            className={cn(
              "flex flex-col gap-1 transition-opacity duration-300",
              isCollapsed ? "opacity-0 hidden" : "opacity-100"
            )}
          >
            <button onClick={handleEditProfile} className="gap-4 flex flex-row">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className="font-medium leading-none">
                      {`${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`
                        ?.length > 8
                        ? `${userInfo?.first_name?.slice(0, 4) ?? ""} ${userInfo?.last_name?.slice(0, 4) ?? ""}...`
                        : `${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`}
                    </h3>
                  </TooltipTrigger>
                  <TooltipContent>
                    {`${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <i className="fa-solid fa-eye"></i>
            </button>
          </div>
        </div>

        <Separator />

        {/* Navigation Items */}
        <nav className="pt-3">
          <NavItem
            icon={<LayoutDashboard className="h-4 w-4 text-primary" />}
            isCollapsed={isCollapsed}
            onClick={() => {
              navigate("user/home");
              setIsCollapsed(true);
            }}
          >
            Home
          </NavItem>
          <NavItem
            icon={<Ticket className="h-4 w-4 text-primary" />}
            isCollapsed={isCollapsed}
            onClick={() => {
              navigate("/user/ticket-listing");
              setIsCollapsed(true);
            }}
          >
            My Tickets
          </NavItem>
            <NavItem
              icon={
                isLoggingOut ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <LogOut className="h-4 w-4 text-primary" />
                )
              }
              isCollapsed={isCollapsed}
              onClick={handleLogout}
              disable={isLoggingOut}
            >
              {isLoggingOut ? "Logging out..." : "Log out"}
            </NavItem>
        </nav>
      </div>
    </div>
  );
}

interface NavItemProps extends React.HTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode;
  isCollapsed: boolean;
  children: React.ReactNode;
  disable?:boolean
}

function NavItem({
  icon,
  isCollapsed,
  children,
  className,
  disable,
  ...props
}: Readonly<NavItemProps>) {
  return (
    <Button
      variant="ghost"
      className={cn(
        "w-full justify-start gap-2 ",
        isCollapsed && "px-2",
        className
      )}
      {...props}
      disabled={disable}
    >
      <div className=" rounded-full p-2 bg-secondary">{icon}</div>
      <span className={cn("transition-opacity  ", isCollapsed && "hidden")}>
        {children}
      </span>
    </Button>
  );
}
